import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";

import _ from "lodash";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import SubHeader from "@cayuse/shared-react.components.sub-header";
import { isNonEmptyArray, isNonEmptyString, isNumber } from "@cayuse/shared-react.utils.type-checks";

import CostCategoriesButtons from "components/BudgetBuilder/BudgetSettings/CostCategoriesList/CostCategoriesButtons";
import CostCategoriesRow from "components/BudgetBuilder/BudgetSettings/CostCategoriesList/CostCategoriesRow";
import CostCategoryModal from "components/BudgetBuilder/BudgetSettings/CostCategoryModal";
import Subsection from "components/Subsection";
import Table from "components/Table";
import { useDictionary } from "hooks";
import { tenantSelectors } from "store/selectors";
import { updateCategories } from "store/actions/tenant";
import { isPresent, moveItemInArrayTo, updateSortOfCategories } from "utils/budgetBuilder";
import { notifyError } from "utils";
import { api } from "services";

import styles from "./styles.css";


const toMoment = dateObject => {
	if (dateObject && isNumber(dateObject)) {
		return moment(new Date(dateObject));
	}
	if (isNonEmptyString(dateObject)) {
		return moment(dateObject);
	}

	return dateObject;
};

const CostCategoriesList = () => {
	const categories = useSelector(tenantSelectors.proposalCategories);
	const dictionary = useDictionary("categories");

	const [ isLoading, setIsLoading ] = useState(false);
	const [ addCategoryModalOpen, setAddCategoryModalOpen ] = useState(false);
	const [ isEditing, setIsEditing ] = useState(false);
	const [ budgetBuilderLastCreatedOn, setBudgetBuilderLastCreatedOn ] = useState(null);
	const [ editedCategories, setEditedCategories ] = useState([]);
	const [ editKey, setEditKey ] = useState(() => uuidv4());
	const isModified = useMemo(
		() => JSON.stringify(categories) !== JSON.stringify(editedCategories),
		[ categories, editedCategories ],
	);

	const isValid = useMemo(
		() => !editedCategories.find(category => {
			const hasValidCodeAndText = isNonEmptyString(category.value) && isNonEmptyString(category.code);
			if (hasValidCodeAndText && isNonEmptyArray(category.items)) {
				return category.items
					.some(subCategory => !isNonEmptyString(subCategory.value) || !isNonEmptyString(subCategory.code));
			}

			return !hasValidCodeAndText;
		}),
		[ editedCategories ]
	);


	const loadBBLastCreatedOn = useCallback(async () => {
		try {
			const response = await api.project.getList({
				page: 0,
				size: 1,
				sort: "createdOn%3Adesc",
			});

			setBudgetBuilderLastCreatedOn(toMoment(response?.content?.[0]?.createdOn));

		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);
		}
	}, []);

	useEffect(() => {
		loadBBLastCreatedOn();
	}, []);

	useEffect(() => {
		setEditedCategories(categories);
	}, [ categories ]);

	const handleSave = () => {
		setIsLoading(true);
		updateCategories(editedCategories, "categories", "PROPOSAL")
			.finally(() => {
				setIsLoading(false);
				setIsEditing(false);
			});
	};

	const handleReset = () => {
		setEditedCategories(categories);
		setIsEditing(false);
	};

	const handleEdit = () => {
		setIsEditing(true);
		setEditKey(uuidv4());
	};

	const handleChange = (hash, field) => {
		setEditedCategories(
			editedCategories.map(cat =>
				cat.hash === hash ? { ...cat, ...field } : cat.items ?
					{
						...cat, items: cat.items.map(item =>
							item.hash === hash ? { ...item, ...field } : item),
					} : cat,
			));
	};

	const handleActiveStatusChange = (hash, newActive) => {
		let parentCategory = editedCategories.find(category => category.hash === hash);
		if (parentCategory && !newActive && isNonEmptyArray(parentCategory.items)) {
			// making a parent inactive also makes all children inactive
			const newItems = parentCategory.items.map(child => ({ ...child, active: newActive }));
			const newParentCategory = { ...parentCategory, active: newActive, items: newItems };
			setEditedCategories(
				editedCategories.map(category => newParentCategory.hash === category.hash ? newParentCategory : category),
			);
		} else if (!parentCategory && newActive) {
			// making a child active while the parent is inactive also makes parent active
			parentCategory = editedCategories
				.find(parent => isNonEmptyArray(parent?.items) && parent.items.some(child => child.hash === hash));
			const newItems = parentCategory.items.map(child =>
				child.hash === hash
					? ({ ...child, active: newActive })
					: child
			);
			const newParentCategory = { ...parentCategory, active: newActive, items: newItems };
			setEditedCategories(
				editedCategories.map(category => newParentCategory.hash === category.hash ? newParentCategory : category),
			);
		} else {
			handleChange(hash, { active: newActive });
		}
	};

	const handleRemoveItem = (index, childIndex) => {
		const updatedCategories = _.cloneDeep(editedCategories);
		if (childIndex === undefined) {
			updatedCategories.splice(index, 1);
		} else {
			updatedCategories[index].items.splice(childIndex, 1);
		}
		setEditedCategories(updatedCategories);
	};

	const addItemToCategories = hash => {
		const newItem = {
			value: "",
			active: true,
			hash: uuidv4(),
			code: "",
			sort: 0,
		};
		let updatedCategories = _.cloneDeep(editedCategories);

		if (hash) {
			const index = updatedCategories.findIndex(cat => cat.hash === hash);
			newItem.active = updatedCategories?.[index]?.active;
			updatedCategories[index].items = [ ...(updatedCategories[index].items || []), newItem ];
		} else {
			updatedCategories.splice(2, 0, newItem);
		}

		setEditedCategories(updateSortOfCategories(updatedCategories));
	};

	const handleAddItem = hash => {
		addItemToCategories(hash);
		setIsEditing(true);
		setAddCategoryModalOpen(false);
	};

	const moveItem = (oldParentPosition, oldChildPosition, parentPosition, childPosition) => {
		if (!isPresent(oldChildPosition) && !isPresent(childPosition)) {
			const updatedList = moveItemInArrayTo(editedCategories, oldParentPosition, parentPosition);
			setEditedCategories(updateSortOfCategories(updatedList));
		} else if (oldParentPosition === parentPosition) {
			const oldItems = editedCategories[parentPosition].items;
			const items = moveItemInArrayTo(oldItems, oldChildPosition, childPosition);
			let newCategories = [ ...editedCategories ];
			newCategories[parentPosition].items = items;
			setEditedCategories(updateSortOfCategories(newCategories));
		}
	};

	const renderTableRow = (category, index, childIndex) => (
		<CostCategoriesRow
			key={`${category.hash}-${editKey}`}
			category={category}
			parentPosition={index}
			childPosition={childIndex}
			isEditing={isEditing}
			budgetBuilderLastCreatedOn={budgetBuilderLastCreatedOn}
			moveItem={moveItem}
			handleActiveStatusChange={handleActiveStatusChange}
			handleChange={handleChange}
			handleRemoveItem={handleRemoveItem}
		/>
	);

	return (
		<Subsection
			title={dictionary.proposal.costCategoryHeader || "Sponsor Cost Categories and Line Items"}
			buttons={CostCategoriesButtons({
				dictionary, isEditing, isModified, isLoading,
				handleReset, handleSave, setAddCategoryModalOpen, isValid, handleEdit,
			})}
			headingType="h3"
		>
			<SubHeader>{dictionary.budgetSettingsCategoryContentHeader || "Budget Categories"}</SubHeader>
			{<div className={styles.divArea}>
				{dictionary.proposal.costCategoryText1 || "The line items in the sponsor costs budget tab are organized into categories. Categories available on first setup, may not be re-named. Other categories, and line items for all categories, may be added and edited to match your institution’s workflow."}
				<p>{dictionary.proposal.costCategoryText2 || "Categories or line items that are not in use by any budgets may be deleted. Ones that are in use may be archived, to remove them from the budget template going forward."}</p>
			</div>}
			<CostCategoryModal
				isOpen={addCategoryModalOpen}
				categories={editedCategories}
				onAdd={handleAddItem}
				onClose={() => setAddCategoryModalOpen(false)}
			/>
			<Table captionText="Cost Categories">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell width="six">
							{dictionary.titleRowHeader || "Name"}
						</Table.HeaderCell>
						<Table.HeaderCell width="six">
							{dictionary.objectCodeColHeader || "Object Code"}
						</Table.HeaderCell>
						<Table.HeaderCell width="six">
							{dictionary.activeRowHeader || "Active Status"}
						</Table.HeaderCell>
						<Table.HeaderCell />
					</Table.Row>
				</Table.Header>
				<Table.Body>
					<DndProvider backend={HTML5Backend}>
						{editedCategories
							.sort((a, b) => a.sort - b.sort)
							.map((category, index) => (
								<Fragment key={index}>
									{renderTableRow(category, index)}
									{category.items && category.items
										.sort((a, b) => a.sort - b.sort)
										.map((item, childIndex) => renderTableRow(item, index, childIndex))}
								</Fragment>
							))}
					</DndProvider>
				</Table.Body>
			</Table>
		</Subsection>
	);
};

export default CostCategoriesList;
